/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import * as _ from 'lodash';

import './SingleAnswerQuestion.scss';
import BackpackSource from './components/BackpackSource';
import SurveyHint from './components/SurveyHint';

import { eSurveyQuestionCategoryName, iSurveyResponse } from '../../../../apiServices/SurveyService';
import { SelectableCardRowWithGroupSurveyTheme } from '../../../components/SelectableRow';

import { Card } from '../../../../bphComponents/bundle';
import OldCard from '../../../components/card/Card';
import { Markdown, NoThemeMarkdown } from '../../../components/Markdown';
import { useSurveyQuestion } from '../../../hooks/useSurveyQuestion';
import COLORS from '../../../styles/Colors';

export const NestedSingleAnswerQuestion = (props) => {
  const { onUpdate, question, response, className, profileId } = props;
  const singleAnswer = question.single_answer;

  const { options } = singleAnswer;
  const responseId = _.get(response, `${eSurveyQuestionCategoryName.singleAnswer}.id`, null);

  return (
    <>
      {
        options.map((o, i) => (
          <SelectableCardRowWithGroupSurveyTheme
            key={i}
            className={`cmp-singleanswer-option font-body ${className}`}
            isSelected={o.response.id === responseId}
            hideBorder={i === options.length - 1}
            onClick={() => {
              onUpdate({
                category: eSurveyQuestionCategoryName.singleAnswer,
                [eSurveyQuestionCategoryName.singleAnswer]: {
                  id: o.response.id,
                },
              }, o);
            }}
          >
            {o.text}

            <BackpackSource source={o.source} profileId={profileId} />

          </SelectableCardRowWithGroupSurveyTheme>
        ))
      }
    </>
  );
};

const SingleAnswerQuestion = (props) => (
  <div className={props.questionClassSelector}>
    <SurveyHint>
      <FormattedMessage id="surveyQuestion.singleAnswer.hint" />
    </SurveyHint>
    <OldCard>
      <NestedSingleAnswerQuestion {...props} />
    </OldCard>
  </div>
);

interface SingleAnswerAnswerProps {
  className: string;
  style: object;
  answer: iSurveyResponse;
  question: any;
  isLast?: boolean;
}

export const SingleAnswerAnswer = (props: SingleAnswerAnswerProps) => {
  const { answer, question } = props;
  const answerId = answer.single_answer.id;
  const option = question[answer.category].options.find((q) => q.response.id === answerId);
  const answerText = _.get(option, 'text', '-');

  return (
    <div className={props.className} style={props.style}>
      {answerText}
    </div>
  );
};

export const CompositeSingleAnswerAnswer = (props: SingleAnswerAnswerProps) => {
  const { answer, question, isLast } = props;
  const answerId = answer.single_answer.id;
  const option = question[answer.category].options.find((q) => q.response.id === answerId);
  const answerText = _.get(option, 'text', '-');

  return (
    <div className={props.className} style={{ ...props.style, marginBottom: isLast ? 0 : 12 }}>
      <span style={{ display: 'inline-block' }}><Markdown text={question.text} /></span> <i style={{ margin: '0 5px' }}>–</i> <span style={{ fontWeight: 'bold' }}>{answerText}</span>
    </div>
  );
};

export const CompositeSingleAnswerQuestion = (props) => {
  const { question, segmentIndex, onUpdate } = props;
  const [, surveyQuestionActions] = useSurveyQuestion();

  const helpText = question[question.category].help_text;

  return (
    <div className={`question-segment-${segmentIndex}`}>
    <div id={props.id} className={props.questionClassSelector} data-segment={segmentIndex} data-index={0}>

      {
        props.isFirst ? (
          <SurveyHint>
            <FormattedMessage id="surveyQuestion.composite.hint" />
          </SurveyHint>
        ) : null
      }

      <OldCard>
        <Card.CardContent disabledPadding={['bottom']} style={{ paddingBottom: 16 }}>
          <NoThemeMarkdown text={props.question.text} />
        </Card.CardContent>

        <NestedSingleAnswerQuestion
          {...props}
          onUpdate={(r, d) => {
            surveyQuestionActions.updateSingleAnswerQuestion(segmentIndex);
            onUpdate(r, d);
          }}
        />

        {
          helpText ? (
            <Card.CardContent disabledPadding={['top']} style={{ backgroundColor: COLORS.csAppBGgray30 }}>
              <Card.CardContent hasTopBorder={true} disabledPadding={['bottom', 'left', 'right']}>
                <NoThemeMarkdown text={helpText} />
              </Card.CardContent>
            </Card.CardContent>
          ) : null
        }

      </OldCard>
    </div>
    </div>
  );
};

export default SingleAnswerQuestion;
